import React, { useState } from "react";
import logo from "../../assets/logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DarkModeToggle from "../darkMode/DarkModeToggle";
import { useAuth } from "../../hooks/useAuth";
import {
  MdDashboard,
  MdAccountBalance,
  MdPayments,
  MdHistory,
  MdPerson,
  MdVerifiedUser,
  MdAdminPanelSettings,
  MdLogout,
  MdMenu,
  MdClose,
} from "react-icons/md";

const DashboardNavbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Icons
  const DashboardIcon = MdDashboard as React.FC<React.SVGProps<SVGSVGElement>>;
  const BalanceIcon = MdAccountBalance as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const PaymentsIcon = MdPayments as React.FC<React.SVGProps<SVGSVGElement>>;
  const HistoryIcon = MdHistory as React.FC<React.SVGProps<SVGSVGElement>>;
  const PersonIcon = MdPerson as React.FC<React.SVGProps<SVGSVGElement>>;
  const VerifyIcon = MdVerifiedUser as React.FC<React.SVGProps<SVGSVGElement>>;
  const AdminIcon = MdAdminPanelSettings as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const LogoutIcon = MdLogout as React.FC<React.SVGProps<SVGSVGElement>>;
  const MenuIcon = MdMenu as React.FC<React.SVGProps<SVGSVGElement>>;
  const CloseIcon = MdClose as React.FC<React.SVGProps<SVGSVGElement>>;

  const menuItems = [
    { path: "/dashboard", icon: DashboardIcon, label: "Overview" },
    { path: "/dashboard/balances", icon: BalanceIcon, label: "Balances" },
    { path: "/dashboard/deposit", icon: PaymentsIcon, label: "Stake Deposit" },
    { path: "/dashboard/withdraw", icon: PaymentsIcon, label: "Withdraw" },
    {
      path: "/dashboard/transactions",
      icon: HistoryIcon,
      label: "Transactions",
    },
    { path: "/dashboard/kyc", icon: VerifyIcon, label: "KYC Verification" },
    { path: "/dashboard/profile", icon: PersonIcon, label: "Profile" },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Main Navbar */}
      <nav className="w-full h-16 bg-white dark:bg-gray-900 shadow-lg fixed top-0 z-50 transition-all duration-300">
        <div className="w-full h-full px-4 lg:px-6 flex items-center justify-between">
          {/* Left Section - Logo & Menu Button */}
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {isSidebarOpen ? (
                <CloseIcon className="w-6 h-6 text-purple-600" />
              ) : (
                <MenuIcon className="w-6 h-6 text-purple-600" />
              )}
            </button>

            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src={logo} alt="Logo" className="w-16 h-16" />
              <div className="hidden md:block">
                <h1 className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  Dashboard
                </h1>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <DarkModeToggle />

            {/* Admin Link */}
            {user?.role === "admin" && (
              <Link
                to="/admin"
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
              >
                <AdminIcon className="w-4 h-4" />
                Admin Panel
              </Link>
            )}

            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Level {user?.account_level} • {user?.role}
                </p>
              </div>
              <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm uppercase">
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </span>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="border border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
            >
              <LogoutIcon className="w-4 h-4" />
              Logout
            </button>
          </div>

          {/* Mobile Right Section */}
          <div className="flex items-center gap-4 lg:hidden">
            <DarkModeToggle />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {isMobileMenuOpen ? (
                <CloseIcon className="w-6 h-6 text-purple-600" />
              ) : (
                <MenuIcon className="w-6 h-6 text-purple-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        <div
          className={`lg:hidden absolute top-16 left-0 w-full bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? "max-h-96 shadow-lg" : "max-h-0"
          }`}
        >
          <div className="flex flex-col p-4 space-y-4">
            {/* User Info */}
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg uppercase">
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-base font-medium text-gray-800 dark:text-gray-200 capitalize">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {user?.email} • Level {user?.account_level}
                </p>
              </div>
            </div>

            {/* Admin Link */}
            {user?.role === "admin" && (
              <Link
                to="/admin"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-yellow-500 hover:bg-yellow-600 text-white w-full justify-start px-4 py-3 rounded-lg transition-all duration-300 flex items-center gap-3"
              >
                <AdminIcon className="w-5 h-5" />
                Admin Panel
              </Link>
            )}

            {/* Logout */}
            <button
              onClick={() => {
                handleLogout();
                setIsMobileMenuOpen(false);
              }}
              className="border border-red-600 text-red-600 hover:bg-red-600 hover:text-white w-full justify-start px-4 py-3 rounded-lg transition-all duration-300 flex items-center gap-3"
            >
              <LogoutIcon className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-40 ${
          isSidebarOpen
            ? "w-64 translate-x-0"
            : "w-64 -translate-x-full lg:translate-x-0 lg:w-20"
        }`}
      >
        <div className="h-full flex flex-col py-6 px-4">
          {/* Navigation */}
          <nav className="space-y-2 flex-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 group ${
                    active
                      ? "bg-purple-600 text-white shadow-lg"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-purple-600 dark:hover:text-purple-400"
                  } ${isSidebarOpen ? "justify-start" : "lg:justify-center"}`}
                >
                  <Icon
                    className={`w-6 h-6 ${isSidebarOpen ? "mr-3" : "lg:mr-0"}`}
                  />
                  <span
                    className={`font-medium transition-all duration-200 ${
                      isSidebarOpen ? "opacity-100" : "lg:opacity-0 lg:absolute"
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* User Info and Logout */}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
            <div
              className={`px-4 py-3 ${
                isSidebarOpen
                  ? "flex items-center space-x-3"
                  : "lg:flex lg:justify-center"
              }`}
            >
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </span>
              </div>
              {isSidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    Level {user?.account_level}
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={handleLogout}
              className={`flex items-center w-full px-4 py-3 mt-2 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group ${
                isSidebarOpen ? "justify-start" : "lg:justify-center"
              }`}
            >
              <LogoutIcon
                className={`w-6 h-6 ${isSidebarOpen ? "mr-3" : "lg:mr-0"}`}
              />
              <span
                className={`font-medium transition-all duration-200 ${
                  isSidebarOpen ? "opacity-100" : "lg:opacity-0 lg:absolute"
                }`}
              >
                Logout
              </span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default DashboardNavbar;
